import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { IoSearch, IoClose, IoAddCircleOutline, IoCreateOutline, IoTrashOutline, IoSparkles, IoArrowBack } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import { useAuth } from "@/context/AuthContext";
import { useAsyncData } from "@/lib/useAsyncData";
import { SkeletonCards, RetryNotice } from "@/components/DataStates";
import { listLexique, lexiqueLetters, getLexique, createLexique, updateLexique, deleteLexique, getPageConfig } from "@/lib/api";

const NERODUS_IMG = "/nerodus.png";

export default function Lexique() {
  const nav = useNavigate();
  const { loading: authLoading, isAuthenticated, isAdmin } = useAuth();
  const { data: all, loading, error, reload, set: setAll } = useAsyncData("lexique", listLexique);
  const [letters, setLetters] = useState([]);
  const [query, setQuery] = useState("");
  const [activeLetter, setActiveLetter] = useState(null);
  const [selected, setSelected] = useState(null);     // terme ouvert (détail)
  const [editor, setEditor] = useState(null);         // admin add/edit
  const [busy, setBusy] = useState(false);
  const [nerodusImg, setNerodusImg] = useState(NERODUS_IMG);
  const refs = useRef({});

  useEffect(() => { if (!authLoading && !isAuthenticated) nav("/connexion", { replace: true, state: { from: { pathname: "/lexique" } } }); }, [authLoading, isAuthenticated, nav]);
  const load = () => { reload(); lexiqueLetters().then((d) => setLetters(d.letters || [])).catch(() => {}); };
  useEffect(() => { lexiqueLetters().then((d) => setLetters(d.letters || [])).catch(() => {}); getPageConfig("nerodus").then((n) => { if (n?.image) setNerodusImg(n.image); }).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = all || [];
    if (q) list = (all || []).filter((d) => (d.term + " " + (d.definition || "")).toLowerCase().includes(q));
    return [...list].sort((a, b) => a.term.localeCompare(b.term, "fr"));
  }, [all, query]);

  const groups = useMemo(() => {
    const g = {};
    filtered.forEach((d) => { const l = d.letter || "#"; (g[l] = g[l] || []).push(d); });
    return g;
  }, [filtered]);
  const groupLetters = Object.keys(groups).sort();

  const jumpTo = (l) => {
    setActiveLetter(l);
    const el = refs.current[l];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openTerm = async (d) => {
    setSelected(d.tip ? d : { ...d, _loadingTip: true });
    if (!d.tip) {
      try { const full = await getLexique(d.id); setSelected(full); setAll((a) => a.map((x) => x.id === full.id ? full : x)); }
      catch { setSelected((s) => s ? { ...s, _loadingTip: false } : s); }
    }
  };

  const saveWord = async () => {
    if (!editor.term.trim() || !editor.definition.trim()) { toast.error("Terme et définition requis"); return; }
    setBusy(true);
    try {
      if (editor.id) await updateLexique(editor.id, { term: editor.term, definition: editor.definition, tip: editor.tip });
      else await createLexique({ term: editor.term, definition: editor.definition, tip: editor.tip || null });
      setEditor(null); load(); toast.success(editor.id ? "Terme mis à jour" : "Terme ajouté ✓");
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };
  const removeWord = async (id) => { if (!window.confirm("Supprimer ce terme du dictionnaire ?")) return; try { await deleteLexique(id); setSelected(null); load(); } catch { toast.error("Échec"); } };

  return (
    <DojoShell crumbs={[{ label: "Bibliothèque", to: "/bibliotheque" }, { label: "Dictionnaire" }]}>
      {/* Bannière immersive */}
      <div style={st.hero} data-testid="lexique-hero">
        <div style={st.heroGlow} />
        <span style={st.heroKanji}>辞</span>
        <div style={{ position: "relative", zIndex: 2 }}>
          <div style={st.heroEyebrow}>Le Lexique du Storyteller</div>
          <h1 style={st.h1}>Le Dictionnaire du Dōjō</h1>
          <p style={st.heroSub}>{(all || []).length} termes de l'art du récit, définis et éclairés d'un conseil de Nerodus. Cherche, explore, inspire-toi.</p>
        </div>
      </div>

      <div style={st.controls}>
        <div style={st.searchWrap}>
          <IoSearch size={18} color="var(--text-secondary)" />
          <input data-testid="lexique-search" style={st.search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Chercher un mot (ex : climax, arc, trope…)" />
          {query && <button style={st.clearBtn} onClick={() => setQuery("")}><IoClose size={16} /></button>}
        </div>
        {isAdmin && <button style={st.addBtn} data-testid="lexique-add" onClick={() => setEditor({ term: "", definition: "", tip: "" })}><IoAddCircleOutline size={18} /> Ajouter un mot</button>}
      </div>

      {/* Rail alphabet */}
      <div style={st.alpha} data-testid="lexique-alphabet">
        {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((l) => {
          const has = letters.includes(l);
          return (
            <button key={l} disabled={!has} data-testid={`lexique-letter-${l}`}
              onClick={() => jumpTo(l)}
              style={{ ...st.alphaBtn, ...(has ? {} : st.alphaOff), ...(activeLetter === l ? st.alphaOn : {}) }}>{l}</button>
          );
        })}
      </div>

      {(loading && !all) ? (
        <div style={{ marginTop: 20 }}><SkeletonCards count={9} height={120} min={220} /></div>
      ) : error ? (
        <div style={{ marginTop: 20 }}><RetryNotice onRetry={reload} label="Impossible de charger le dictionnaire." /></div>
      ) : filtered.length === 0 ? (
        <p style={{ color: "var(--text-secondary)", marginTop: 20 }}>Aucun terme pour « {query} ».</p>
      ) : (
        <div style={st.scroll}>
          {groupLetters.map((l) => (
            <section key={l} ref={(el) => (refs.current[l] = el)} style={st.section}>
              <div style={st.sectionLetter}>{l}</div>
              <div style={st.termGrid}>
                {groups[l].map((d) => (
                  <button key={d.id} style={st.termCard} data-testid={`lexique-term-${d.id}`} onClick={() => openTerm(d)}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.borderColor = RED; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.borderColor = "var(--border-c)"; }}>
                    <div style={st.termName}>{d.term}</div>
                    <div style={st.termDef}>{d.definition}</div>
                    <span style={st.termMore}>Lire <IoSparkles size={12} /></span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Détail terme */}
      {selected && (
        <div style={st.overlay} onClick={() => setSelected(null)}>
          <div style={st.sheet} onClick={(e) => e.stopPropagation()} data-testid="lexique-detail">
            <button style={st.sheetBack} onClick={() => setSelected(null)} data-testid="lexique-detail-close"><IoArrowBack size={16} /> Fermer</button>
            <div style={st.sheetTermRow}>
              <h2 style={st.sheetTerm}>{selected.term}</h2>
              {isAdmin && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={st.miniBtn} title="Modifier" onClick={() => { setEditor(selected); setSelected(null); }} data-testid="lexique-edit"><IoCreateOutline size={16} /></button>
                  <button style={{ ...st.miniBtn, color: RED }} title="Supprimer" onClick={() => removeWord(selected.id)} data-testid="lexique-delete"><IoTrashOutline size={15} /></button>
                </div>
              )}
            </div>
            <p style={st.sheetDef}>{selected.definition}</p>

            <div style={st.nerodusBox} data-testid="lexique-tip">
              <div style={st.nerodusHead}>
                <span style={st.nerodusAvatar}><img src={nerodusImg} alt="Nerodus" style={st.nerodusImg} /></span>
                <div style={{ flex: 1 }}>
                  <div style={st.nerodusName}>Le mot de Nerodus</div>
                  <div style={st.nerodusRole}>Bibliothécaire du Dōjō</div>
                </div>
              </div>
              {selected._loadingTip ? (
                <p style={st.nerodusLoading}><IoSparkles size={14} /> Un instant…</p>
              ) : (
                <p style={st.nerodusText}>« {selected.tip} »</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Éditeur admin */}
      {editor && (
        <div style={st.overlay} onClick={() => setEditor(null)}>
          <div style={st.sheet} onClick={(e) => e.stopPropagation()} data-testid="lexique-editor">
            <div style={st.sheetTermRow}>
              <h2 style={st.sheetTerm}>{editor.id ? "Modifier le terme" : "Nouveau terme"}</h2>
              <button style={st.miniBtn} onClick={() => setEditor(null)}><IoClose size={18} /></button>
            </div>
            <label style={st.lbl}>Terme</label>
            <input style={st.input} value={editor.term} onChange={(e) => setEditor({ ...editor, term: e.target.value })} placeholder="Ex : Climax" data-testid="lexique-form-term" autoFocus />
            <label style={st.lbl}>Définition</label>
            <textarea style={{ ...st.input, minHeight: 100, resize: "vertical" }} value={editor.definition} onChange={(e) => setEditor({ ...editor, definition: e.target.value })} placeholder="La définition claire du terme…" data-testid="lexique-form-def" />
            <label style={st.lbl}>Conseil de Nerodus <span style={{ fontWeight: 500, color: "var(--text-secondary)" }}>(laisse vide → généré par l'IA)</span></label>
            <textarea style={{ ...st.input, minHeight: 80, resize: "vertical" }} value={editor.tip || ""} onChange={(e) => setEditor({ ...editor, tip: e.target.value })} placeholder="Laisse vide pour que Nerodus l'écrive automatiquement." data-testid="lexique-form-tip" />
            <div style={st.sheetActions}>
              <button style={st.cancel} onClick={() => setEditor(null)}>Annuler</button>
              <button style={{ ...st.addBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={saveWord} data-testid="lexique-form-save">{busy ? "…" : "Enregistrer"}</button>
            </div>
          </div>
        </div>
      )}
    </DojoShell>
  );
}

const st = {
  hero: { position: "relative", overflow: "hidden", borderRadius: 22, padding: "34px 30px", marginBottom: 20,
    background: "linear-gradient(135deg, #1a0b0d 0%, #2a1114 55%, #140a1e 100%)", border: "1px solid rgba(225,29,42,0.35)" },
  heroGlow: { position: "absolute", top: -80, right: -40, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(225,29,42,0.4), transparent 70%)", filter: "blur(10px)" },
  heroKanji: { position: "absolute", right: 24, bottom: -18, fontSize: 150, fontWeight: 900, color: "rgba(255,255,255,0.06)", lineHeight: 1, zIndex: 1 },
  heroEyebrow: { color: "#ffb3ba", fontSize: 12.5, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase" },
  h1: { fontSize: 34, fontWeight: 900, margin: "6px 0 0", color: "#fff", fontFamily: "var(--font-display)" },
  heroSub: { color: "rgba(255,255,255,0.72)", fontSize: 14.5, margin: "10px 0 0", maxWidth: 560, lineHeight: 1.6 },
  controls: { display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" },
  searchWrap: { flex: 1, minWidth: 220, display: "flex", alignItems: "center", gap: 10, height: 48, padding: "0 16px", borderRadius: 14, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)" },
  search: { flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 15, color: "var(--text-primary)", fontFamily: "inherit" },
  clearBtn: { border: "none", background: "transparent", color: "var(--text-secondary)", cursor: "pointer", display: "flex" },
  addBtn: { display: "inline-flex", alignItems: "center", gap: 7, padding: "0 18px", height: 48, borderRadius: 14, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" },
  alpha: { display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 20, padding: "10px 12px", borderRadius: 14, background: "var(--bg-secondary)", border: "1px solid var(--border-c)" },
  alphaBtn: { minWidth: 30, height: 30, borderRadius: 8, border: "none", background: "transparent", color: "var(--text-primary)", fontWeight: 800, fontSize: 13, cursor: "pointer" },
  alphaOff: { color: "var(--text-secondary)", opacity: 0.3, cursor: "default" },
  alphaOn: { background: RED, color: "#fff" },
  scroll: { display: "flex", flexDirection: "column", gap: 28 },
  section: { scrollMarginTop: 90 },
  sectionLetter: { fontSize: 30, fontWeight: 900, color: RED, fontFamily: "var(--font-display)", marginBottom: 12, borderBottom: "2px solid var(--border-c)", paddingBottom: 6 },
  termGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 14 },
  termCard: { display: "flex", flexDirection: "column", textAlign: "left", padding: "16px 18px", borderRadius: 16, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", cursor: "pointer", transition: "transform .18s ease, border-color .18s ease", boxShadow: "var(--surface-shadow)" },
  termName: { fontWeight: 900, fontSize: 17, color: "var(--text-primary)", fontFamily: "var(--font-display)" },
  termDef: { fontSize: 13, color: "var(--text-secondary)", marginTop: 6, lineHeight: 1.55, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" },
  termMore: { marginTop: 10, color: RED, fontWeight: 800, fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 5 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 1000 },
  sheet: { width: "100%", maxWidth: 560, maxHeight: "88vh", overflowY: "auto", background: "var(--bg-primary)", borderRadius: 20, padding: "22px 24px", border: "1px solid var(--border-c)", boxShadow: "0 30px 80px rgba(0,0,0,0.4)", animation: "lexPop .3s ease both" },
  sheetBack: { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 999, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13, cursor: "pointer", marginBottom: 14 },
  sheetTermRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  sheetTerm: { fontSize: 26, fontWeight: 900, margin: 0, color: "var(--text-primary)", fontFamily: "var(--font-display)" },
  sheetDef: { fontSize: 15.5, color: "var(--text-primary)", lineHeight: 1.7, margin: "14px 0 18px" },
  nerodusBox: { borderRadius: 18, padding: "18px 20px", background: "linear-gradient(135deg, color-mix(in srgb, var(--brand) 12%, transparent), color-mix(in srgb, var(--brand) 4%, transparent))", border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)", animation: "lexFade .4s ease both" },
  nerodusHead: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  nerodusAvatar: { width: 52, height: 52, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `2px solid ${RED}`, boxShadow: `0 6px 18px ${RED}44` },
  nerodusImg: { width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" },
  nerodusName: { fontWeight: 900, color: RED, fontSize: 15.5, fontFamily: "var(--font-display)" },
  nerodusRole: { fontSize: 11.5, color: "var(--text-secondary)", fontWeight: 700 },
  nerodusText: { fontSize: 15, color: "var(--text-primary)", lineHeight: 1.7, margin: 0, fontStyle: "italic" },
  nerodusLoading: { fontSize: 14, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 8, margin: 0 },
  miniBtn: { width: 36, height: 36, borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  lbl: { display: "block", fontSize: 12, fontWeight: 800, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.4, margin: "14px 0 6px" },
  input: { width: "100%", padding: "11px 14px", borderRadius: 12, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 14.5, fontFamily: "inherit", boxSizing: "border-box" },
  sheetActions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 },
  cancel: { padding: "11px 18px", borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 700, cursor: "pointer" },
};

if (typeof document !== "undefined" && !document.getElementById("lex-kf")) {
  const el = document.createElement("style");
  el.id = "lex-kf";
  el.innerHTML = "@keyframes lexPop{from{opacity:0;transform:translateY(16px) scale(.98)}to{opacity:1;transform:none}}@keyframes lexFade{from{opacity:0}to{opacity:1}}";
  document.head.appendChild(el);
}
